"use client";

import { useState } from "react";
import { useRouter } from "next/navigation";
import { SignedIn, useUser } from "@clerk/nextjs";
import { useMutation } from "convex/react";
import { toast } from "sonner";

import { api } from "@soonlist/backend/convex/_generated/api";
import { Button } from "@soonlist/ui/button";

import { Archive, ArchiveRestore, Trash } from "~/components/icons";
import { DropdownMenuItem } from "./DropdownMenu";

export interface DeleteButtonProps {
  userId: string;
  id: string;
  type: "icon" | "button" | "dropdown";
}

function useIsOwner(userId: string) {
  const { user } = useUser();
  const roles = user?.publicMetadata.roles as string[] | undefined;
  return user?.id === userId || roles?.includes("admin");
}

export function ArchiveButton(props: DeleteButtonProps) {
  const isOwner = useIsOwner(props.userId);
  const archiveEvent = useMutation(api.events.archive);
  const [isPending, setIsPending] = useState(false);

  const handleArchive = async () => {
    setIsPending(true);
    try {
      await archiveEvent({ id: props.id });
      toast.success("Event archived. The link still works.");
    } catch (error) {
      console.error(error);
      toast.error("Your event was not archived. Please try again.");
    } finally {
      setIsPending(false);
    }
  };

  if (!isOwner) {
    return null;
  }

  if (props.type === "icon") {
    return (
      <SignedIn>
        <Button
          size={"icon"}
          variant={"ghost"}
          onClick={handleArchive}
          disabled={isPending}
          aria-label="Archive event"
        >
          <Archive className="size-4 text-neutral-2" aria-hidden="true" />
        </Button>
      </SignedIn>
    );
  }

  return (
    <SignedIn>
      <DropdownMenuItem onSelect={handleArchive} disabled={isPending}>
        <Archive className="mr-2 size-4" />
        Archive
      </DropdownMenuItem>
    </SignedIn>
  );
}

export function RestoreButton(props: DeleteButtonProps) {
  const isOwner = useIsOwner(props.userId);
  const unarchiveEvent = useMutation(api.events.unarchive);
  const [isPending, setIsPending] = useState(false);

  const handleRestore = async () => {
    setIsPending(true);
    try {
      await unarchiveEvent({ id: props.id });
      toast.success("Event restored.");
    } catch (error) {
      console.error(error);
      toast.error("Your event was not restored. Please try again.");
    } finally {
      setIsPending(false);
    }
  };

  if (!isOwner) {
    return null;
  }

  return (
    <SignedIn>
      <Button
        size={"icon"}
        variant={"ghost"}
        onClick={handleRestore}
        disabled={isPending}
        aria-label="Restore event"
      >
        <ArchiveRestore
          className="size-4 text-interactive-1"
          aria-hidden="true"
        />
      </Button>
    </SignedIn>
  );
}

export function DeleteButton(props: DeleteButtonProps) {
  const { user } = useUser();
  const isOwner = useIsOwner(props.userId);
  const router = useRouter();
  const deleteEvent = useMutation(api.events.deleteEvent);
  const [isDeleting, setIsDeleting] = useState(false);

  const handleDelete = async () => {
    if (
      !window.confirm(
        "Permanently delete this event? Its link will stop working.",
      )
    ) {
      return;
    }
    setIsDeleting(true);
    try {
      await deleteEvent({ id: props.id });
      toast.success("Event deleted.");
      if (user?.username) {
        router.push(`/${user.username}/upcoming`);
        router.refresh();
      } else {
        router.push("/");
        router.refresh();
      }
    } catch (error) {
      console.error(error);
      toast.error("Your event was not deleted. Please try again.");
    } finally {
      setIsDeleting(false);
    }
  };

  if (!isOwner) {
    return null;
  }

  if (props.type === "icon") {
    return (
      <SignedIn>
        <Button
          size={"icon"}
          variant={"ghost"}
          onClick={handleDelete}
          disabled={isDeleting}
          aria-label="Delete event"
        >
          <Trash className="size-4 text-red-600" aria-hidden="true" />
        </Button>
      </SignedIn>
    );
  }

  return (
    <SignedIn>
      <DropdownMenuItem
        onSelect={handleDelete}
        disabled={isDeleting}
        className="text-red-600"
      >
        <Trash className="mr-2 size-4" />
        Delete
      </DropdownMenuItem>
    </SignedIn>
  );
}
